/** @jsx jsx */

import {
  Fragment as _,
  useContext,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import { keyframes } from 'emotion';
import { css, jsx } from '@emotion/core';
import EditMarker from 'components/UI/EditMarker';
import { colors, getButtonFocus } from 'styles/variables';
import getBlockById from 'services/apiHelpers/getBlockById';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import ReactPlayer from 'react-player';
import VideoIcon from 'assets/Video.svg';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { interactionContext } from 'components/lib/TextAndImage/interactionContext';
import { unlockInteraction } from 'services/unlockInteraction';
import Transcript from './Transcript';
import { Block } from '@chameleoncreator/creator-app';
import { Stream } from '@cloudflare/stream-react';
import { killAllAudio } from 'services/killAllAudio';

const { lPink } = colors;

const frameSelected = keyframes`
  from {
    width: 50%;
    height: 50%;
  }
  to {
    width: 75%;
    height: 75%;
  }
`;

const ShowTranscriptBtn = ({
  id,
  setShowTranscript,
  showTranscriptFontSize = 12,
  showTranscriptFontColor = '#000',
  showTranscriptLabel = 'Show transcript',
}) => {
  return (
    <button
      id={`show-transcript-${id}`}
      css={css`
        width: 100%;

        font-size: ${showTranscriptFontSize}px;

        border: none;
        outline: none;
        background: transparent;

        text-decoration: underline;
        text-align: center;
        cursor: pointer;

        color: ${showTranscriptFontColor};
        &:focus {
          ${getButtonFocus('#2684FF')}
        }
      `}
      onClick={() => {
        setShowTranscript(true);
      }}
    >
      {showTranscriptLabel}
    </button>
  );
};

const EditWrapper = ({ id, selected }) => {
  return (
    <div
      css={css`
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        left: 0px;
        top: 0px;
        width: 100%;
        height: 100%;
        pointer-events: none;
      `}
    >
      <div
        css={css`
position: relative;
display: flex;
justify-content: center;
align-items: center;
width: ${selected ? '75%' : '50%'};
height: ${selected ? '75%' : '50%'};

${selected && `animation: ${frameSelected} 0.2s forwards;`}

${!selected &&
  `
background: rgba(0, 0, 0, 0.1);
`}

box-shadow: ${selected ? `0 0 0 1px ${lPink}` : '0 0 0 1px lightGrey'};
transition: all 0.2s;
pointer-events: all;
cursor: pointer;
&:hover {
  ${!selected && `box-shadow: 0 0 0 1px ${lPink};`}
}
`}
        onClick={e => {
          e.stopPropagation();
          if (selected) return;
          setCurrentBlock({ id });
        }}
      >
        {!selected && (
          <img src={VideoIcon} style={{ width: 50, opacity: 0.5 }} />
        )}

        {!selected && <EditMarker />}
      </div>
    </div>
  );
};

const Video = ({
  id,
  focusedNodeId,
  children,
  ...props
}: {
  id?: any;
  focusedNodeId?: any;
  children?: any;
}) => {
  const { entry, init } = useIntersectionObserver(true);
  const {
    src,
    videoSrc,
    maxWidth = 960,
    hideControls,
    useTranscript,
    showTranscriptFontSize = 12,
    showTranscriptFontColor = '#000',
    showTranscriptLabel = 'Show transcript',
    viewingTranscriptUnlocksInteraction = false,
    cloudflareUid,
    // = '5cf27df9948930e8611e2a27c584b40e',
    posterSrc,
    playInline = false,
  } = getBlockById(id).props!;

  const [isPlaying, setIsPlaying] = useState(false);
  const [calcHeight, setCalcHeight] = useState(maxWidth * 0.5625);

  const [showTranscript, setShowTranscript] = useState(false);
  const [showPoster, setShowPoster] = useState<boolean | null>(posterSrc);

  const selected = focusedNodeId === id;
  const context = useContext(interactionContext)! as Block;
  const mounted = useRef(false);
  const video = useRef<HTMLDivElement>(null);

  // need to get the correct type for player
  const player = useRef<any>(null);

  useLayoutEffect(() => {
    if (!mounted.current) {
      init([video.current], {
        threshold: 0.5,
      });
    }

    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const onWindowResize = () => {
      const { width } = video.current!.getBoundingClientRect();
      const calcHeightTarget = width * 0.5625;

      if (calcHeight === calcHeightTarget) {
        return;
      }

      setCalcHeight(calcHeightTarget);
    };

    onWindowResize();
    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  });

  useEffect(() => {
    // pauses cloudflare player
    //
    if (!cloudflareUid || !player.current) {
      return;
    }

    if (isPlaying && entry && !entry.isIntersecting) {
      player.current.pause();
    }
  }, [entry?.isIntersecting]);

  useEffect(() => {
    if (isPlaying) {
      killAllAudio();
    }
  }, [isPlaying]);

  useEffect(() => {
    const onFullScreenChange = e => {
      if (!document.fullscreenElement) {
        const section = document.querySelector(`#i${context.id}`);
        section?.scrollIntoView();
      }
    };

    video?.current?.addEventListener('fullscreenchange', onFullScreenChange);

    return () => {
      video?.current?.removeEventListener(
        'fullscreenchange',
        onFullScreenChange
      );
    };
  }, []);

  useEffect(() => {
    if (viewingTranscriptUnlocksInteraction && showTranscript) {
      unlockInteraction(context.id);
    }
  }, [showTranscript]);

  const continuePlayingInFullscreen =
    isPlaying && document.fullscreenElement !== null ? true : false;

  const videoPlayer = (
    <div
      ref={video}
      className="video-player-container"
      css={css`
        position: relative;
        width: 100%;
        max-width: ${maxWidth}px;
        // height: ${calcHeight ? `${calcHeight}px` : '100%'};
        // margin: 50px 0;
      `}
      // i want to keep this on the periphery i think
      // if we're not happy with the behaviours we're getting out of the box for these players
      // ( reactPlayer feels kinda janky )
      // if we roll our own solution we can standardise the output
      // onMouseOver={() => {
      //   setShowPoster(false);
      // }}
      // onMouseOut={() => {
      //   if (player.current.getCurrentTime() === 0) {
      //     setShowPoster(true);
      //   }
      // }}
    >
      <_>
        {cloudflareUid ? (
          <CloudflareVideo
            streamRef={player}
            id={id}
            cloudflareUid={cloudflareUid}
            height={calcHeight ? `${calcHeight}px` : '100%'}
            onPlay={() => {
              setIsPlaying(true);
            }}
            onPause={() => {
              setIsPlaying(false);
            }}
            onEnded={() => {
              unlockInteraction(context.id);
            }}
            controls={!hideControls}
            poster={posterSrc}
            {...(playInline && {
              autoplay: true,
              loop: true,
              muted: true,
              playsinline: true,
              controls: false,
            })}
          />
        ) : (
          <ReactPlayer
            ref={player}
            url={videoSrc || src}
            width="100%"
            height={calcHeight || '100%'}
            onStart={() => {}}
            onPlay={() => {
              setIsPlaying(true);
            }}
            onPause={() => {
              setIsPlaying(false);
            }}
            onEnded={() => {
              unlockInteraction(context.id);
            }}
            playing={
              continuePlayingInFullscreen ||
              (isPlaying && entry && entry.isIntersecting)
            }
            controls={!hideControls}
            light={posterSrc}
            {...(playInline && {
              playing: true,
              loop: true,
              muted: true,
              playsinline: true,
              controls: false,
            })}
          />
        )}
      </_>

      {window.dev() && <EditWrapper id={id} selected={selected} />}
    </div>
  );

  const showTranscriptBtn = (
    <div style={{ margin: '5px 0 0 0' }}>
      <ShowTranscriptBtn
        id={id}
        setShowTranscript={setShowTranscript}
        showTranscriptFontSize={showTranscriptFontSize}
        showTranscriptFontColor={showTranscriptFontColor}
        showTranscriptLabel={showTranscriptLabel}
      />
    </div>
  );

  return (
    <_>
      {window.dev() ? (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            left: 0px;
            top: 0px;
            width: 100%;
            // height: ${calcHeight}px;

            // * {
            //   max-height: ${calcHeight}px;
            // }
          `}
        >
          {videoPlayer}

          {useTranscript && showTranscriptBtn}
        </div>
      ) : (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
          `}
        >
          {videoPlayer}

          {useTranscript && showTranscriptBtn}
        </div>
      )}

      {showTranscript && (
        <Transcript
          onClose={() => {
            const tick = setInterval(() => {
              const showTranscriptButtonElement: any = document.querySelector(
                `#show-transcript-${id}`
              );

              if (showTranscriptButtonElement) {
                showTranscriptButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            setShowTranscript(false);
          }}
        >
          {children}
        </Transcript>
      )}
    </_>
  );
};

export const CloudflareVideo = props => {
  const { id, cloudflareUid, ...rest } = props;
  const { height } = rest;

  return (
    <div
      css={css({
        height: height,
        '*': {
          maxHeight: height,
          // pointerEvents: window.dev() ? 'none' : 'auto',
        },
      })}
    >
      <Stream
        controls
        src={cloudflareUid}
        {...{
          ...rest,
          //
        }}
      />
    </div>
  );
};

export default Video;
